import { KlearDate } from '@klear/date'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { ROUTE_TRANSFERS, ROUTE_TRANSFER } from '@klear/constants'
import { Button } from '@klear/ui/components/button'
import { MoreHorizontal, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { Payment } from '@klear/payment/payment'
import { AgGridReact } from 'ag-grid-react'
import {
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ValueFormatterParams,
  type ValueGetterParams,
  type ICellRendererParams,
  type ProcessCellForExportParams
} from 'ag-grid-community'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import { trpc } from '@klear/ui/lib/trpc'
import constant from 'lodash/constant'
import ViewIcon from '@/icons/ViewIcon'
import ExportIcon from '@/icons/ExportIcon'
import SyncStripePaymentsButton from '@/components/SyncStripePaymentsButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@klear/ui/components/dropdown-menu'
import { useMutationWithToast } from '@klear/ui/hooks/use-mutations-with-toast'
import { useTransferList } from '@/app/transfers/use-transfers'
import { paymentIsFailed } from '@klear/payment/status'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export default function Transfers() {
  useDocumentTitle('Transfers')
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const utils = trpc.useUtils()
  const agGrid = useKlearAgGrid()

  const { transfers: payments, isLoading } = useTransferList()

  const { mutate: retryPayment, isPending: isRetrying } = useMutationWithToast(
    trpc.payment.retry.useMutation,
    {
      successTitle: 'Transfer scheduled for retry',
      errorTitle: 'Failed to retry transfer'
    },
    {
      onSuccess: () => utils.payment.list.invalidate()
    }
  )

  const { mutate: deletePayment, isPending: isDeleting } = useMutationWithToast(
    trpc.payment.delete.useMutation,
    {
      successTitle: 'Transfer deleted',
      errorTitle: 'Failed to delete transfer'
    },
    {
      onSuccess: () => utils.payment.list.invalidate()
    }
  )

  const ActionCellRenderer = (props: ICellRendererParams<Payment>) => {
    const payment = props.data
    if (!payment) return null

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to={`/${ROUTE_TRANSFERS}/${payment.id}`} className="flex items-center gap-2">
              <ViewIcon />
              View
            </Link>
          </DropdownMenuItem>
          {paymentIsFailed(payment.status) && (
            <DropdownMenuItem
              onClick={() => retryPayment({ id: payment.id })}
              disabled={isRetrying}
              className="flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Retry Transfer
            </DropdownMenuItem>
          )}
          {payment.canDelete && (
            <DropdownMenuItem
              onClick={() => deletePayment({ id: payment.id })}
              disabled={isDeleting}
              className="flex items-center gap-2 text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Delete Transfer
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const columnDefs = useMemo<ColDef<Payment>[]>(
    () => [
      {
        headerName: 'Transfer ID',
        field: 'id'
      },
      {
        headerName: 'Date',
        field: 'effectiveDate'
      },
      {
        headerName: 'Amount',
        field: 'amount'
      },
      {
        headerName: 'Network',
        colId: 'paymentNetwork',
        field: 'paymentNetwork',
        type: 'text',
        valueGetter: (params: ValueGetterParams<Payment>) => params.data?.paymentNetworkLabel ?? ''
      },
      { headerName: 'From', field: 'sourceAccountOrganizationName' },
      { headerName: 'To', field: 'destinationAccountOrganizationName' },
      {
        headerName: 'Status',
        field: 'status',
        valueFormatter: (params: ValueFormatterParams<Payment>) => params.data?.statusLabel ?? '-'
      },
      {
        headerName: 'Stripe Error',
        field: 'stripePaymentErrorMessage',
        type: 'text',
        valueGetter: (params: ValueGetterParams<Payment>) => params.data?.stripePaymentErrorMessage?.trim() || '',
        valueFormatter: (params: ValueFormatterParams<Payment>) => params.data?.stripePaymentErrorMessage?.trim() || '-'
      },
      { headerName: 'Payment Record Created by', field: 'createdByEmail' },
      { headerName: 'Internal Notes', field: 'internalNotes' },
      {
        headerName: '',
        colId: 'actions',
        type: 'actions',
        cellRenderer: ActionCellRenderer,
        pinned: 'right',
        menuTabs: [],
        valueGetter: constant(null),
        cellStyle: { overflow: 'visible' },
        flex: 0,
        width: 60,
        minWidth: 60,
        maxWidth: 60
      }
    ],
    [deletePayment, isDeleting, isRetrying, retryPayment]
  )

  const defaultColDef = useMemo(
    () => ({
      ...agGrid.defaultColDef,
      flex: 1
    }),
    [agGrid.defaultColDef]
  )

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api)
    if (isLoading) {
      params.api.showLoadingOverlay()
    }
  }

  const handleExport = () => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: `transfers_export_${KlearDate.today().toJSON()}.csv`,
        columnKeys: [
          'id',
          'effectiveDate',
          'amount',
          'paymentNetwork',
          'sourceAccountOrganizationName',
          'destinationAccountOrganizationName',
          'status',
          'stripePaymentErrorMessage',
          'createdByEmail',
          'internalNotes'
        ],
        processCellCallback: (params: ProcessCellForExportParams<Payment>) => {
          const data = params.node?.data
          if (data && params.column.getColId() === 'amount') {
            return data.amount.toString({ style: 'plain' })
          }
          return params.formatValue(params.value) ?? ''
        }
      })
    }
  }

  useEffect(() => {
    if (!gridApi) return
    if (isLoading) {
      gridApi.showLoadingOverlay()
      return
    }
    if (payments.length > 0) {
      gridApi.hideOverlay()
    } else {
      gridApi.showNoRowsOverlay()
    }
  }, [gridApi, isLoading, payments])

  return (
    <div className="container mx-auto px-4 py-4 h-full flex flex-col gap-4 min-h-0">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Transfers</h2>
        </div>
      </div>
      <div className="flex items-start justify-end gap-2 flex-wrap">
        <SyncStripePaymentsButton />
        <Button onClick={handleExport} variant="outline" disabled={!gridApi || payments.length === 0}>
          <ExportIcon />
          <span>Export CSV</span>
        </Button>
        <Button asChild>
          <Link to={`/${ROUTE_TRANSFERS}/${ROUTE_TRANSFER}`}>
            <Plus className="mr-2 h-4 w-4" />
            Add Transfer
          </Link>
        </Button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="w-full h-full min-h-[600px]">
          <AgGridReact
            rowHeight={35}
            headerHeight={40}
            rowData={payments}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            columnTypes={agGrid.columnTypes}
            dataTypeDefinitions={agGrid.dataTypeDefinitions}
            animateRows={true}
            enableCellTextSelection={false}
            pagination={true}
            paginationPageSize={20}
            theme={agGrid.theme}
            quickFilterText=""
            onGridReady={onGridReady}
          />
        </div>
      </div>
    </div>
  )
}
